import { useCallback, useEffect, useState } from 'react';
import { ADMIN_PAGE } from '@twinden/shared';
import { useLoadingState } from '../hooks';
import { useDebounced, usePagedList } from '../hooks/usePagedList';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Copy, Trash2 } from 'lucide-react';
import {
  adminApi,
  ApiError,
  type AdminDeletedFamily,
  type AdminFamily,
  type AdminFamilyDetail,
  type AdminUser,
  type AdminAppModes,
  type AdminAuditEntry,
  type EnrollmentTokenCreated,
} from '../lib/api';
import { auditActionLabel } from '../lib/audit';
import { LogoMark } from '../components/brand/Logo';
import { TwindenQr } from '../components/brand/TwindenQr';
import { CostsTab } from '../components/admin/CostsTab';
import { RequestsTab } from '../components/admin/RequestsTab';
import { DemoTag, ReviewTab } from '../components/admin/ReviewTab';
import { IdeasTab } from '../components/admin/IdeasTab';
import { ReportsTab } from '../components/admin/ReportsTab';
import { LawfulRequestsTab } from '../components/admin/LawfulRequestsTab';
import { HealthTab } from '../components/admin/HealthTab';
import {
  AdminSearch,
  ListCount,
  LoadMore,
} from '../components/admin/AdminPaging';
import { appLocale } from '../lib/format';

// ---------- shared bits ----------

function QrModal({
  enrollment,
  isOpen,
  onClose,
}: {
  enrollment: EnrollmentTokenCreated | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  if (!enrollment) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Enrollment QR</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Show this to the person once. It stops working after use.
            </Text>
            <TwindenQr
              value={enrollment.joinUrl}
              label="Enrollment QR code"
              size="280px"
            />
            <HStack w="full">
              <Input value={enrollment.joinUrl} isReadOnly size="sm" />
              <IconButton
                aria-label="Copy link"
                icon={<Copy size={16} />}
                onClick={() => {
                  navigator.clipboard.writeText(enrollment.joinUrl);
                  toast({
                    title: 'Link copied',
                    status: 'success',
                    duration: 2000,
                  });
                }}
              />
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function statusColor(status: string) {
  switch (status) {
    case 'pending':
      return 'green';
    case 'consumed':
      return 'blue';
    case 'revoked':
      return 'red';
    case 'expired':
      return 'gray';
    default:
      return 'gray';
  }
}

// ---------- Deleted families (M54, D191) ----------

const fetchDeletedFamilies = async (cursor: string | null) =>
  (await adminApi.getDeletedFamilies({ cursor })).data;

// What's left of families their roots deleted: name, size, who and when.
// 50 a page (#73).
function DeletedFamilies() {
  const list = usePagedList<
    AdminDeletedFamily,
    Awaited<ReturnType<typeof fetchDeletedFamilies>>
  >(fetchDeletedFamilies);
  const rows = list.items;
  if (!rows || rows.length === 0) return null;
  return (
    <Box overflowX="auto">
      <Heading size="sm" mb={2}>
        Deleted families
      </Heading>
      <ListCount list={list} one="deleted family" many="deleted families" />
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Members</Th>
            <Th>Deleted by</Th>
            <Th>Deleted</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr key={row.id}>
              <Td>{row.name}</Td>
              <Td>{row.memberCount}</Td>
              <Td>{row.deletedBy?.displayName ?? '—'}</Td>
              <Td>{new Date(row.deletedAt).toLocaleDateString(appLocale())}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <LoadMore list={list} />
    </Box>
  );
}

// ---------- Families tab ----------

function FamiliesTab({ openFamilyId }: { openFamilyId?: string | null }) {
  const toast = useToast();
  // Search by name or handle (D217), on the server, 300 ms after the last
  // keystroke; 50 a page (#73).
  const [query, setQuery] = useState('');
  const search = useDebounced(query.trim(), ADMIN_PAGE.SEARCH_DEBOUNCE_MS);
  const fetchPage = useCallback(
    async (cursor: string | null) =>
      (await adminApi.getFamilies({ cursor, q: search })).data,
    [search]
  );
  const list = usePagedList<AdminFamily, Awaited<ReturnType<typeof fetchPage>>>(
    fetchPage
  );
  const families = list.items;
  const [selected, setSelected] = useState<AdminFamilyDetail | null>(null);
  const [qrEnrollment, setQrEnrollment] =
    useState<EnrollmentTokenCreated | null>(null);
  const selectedRowBg = 'accent.tint';
  const detailBg = 'surface';

  const createModal = useDisclosure();
  const inviteModal = useDisclosure();
  const qrModal = useDisclosure();

  // create-family form
  const [famName, setFamName] = useState('');
  const [famDesc, setFamDesc] = useState('');
  // No handle for the first root (D348): they pick theirs on the link page.
  const [rootDisplayName, setRootDisplayName] = useState('');

  // invite form
  // Name and role only (D348).
  const [invDisplayName, setInvDisplayName] = useState('');
  const [invRole, setInvRole] = useState<'root' | 'admin' | 'member'>('member');
  const [invDays, setInvDays] = useState(7);

  const loadFamilies = list.reload;

  const loadFamily = useCallback(async (id: string) => {
    const res = await adminApi.getFamily(id);
    setSelected(res.data);
  }, []);

  // A report's family link opens it here (M58, D210).
  useEffect(() => {
    if (openFamilyId) loadFamily(openFamilyId).catch(() => undefined);
  }, [openFamilyId, loadFamily]);

  const showQr = (enrollment: EnrollmentTokenCreated) => {
    setQrEnrollment(enrollment);
    qrModal.onOpen();
  };

  const handleCreateFamily = async () => {
    try {
      const res = await adminApi.createFamily({
        name: famName,
        description: famDesc || undefined,
        rootDisplayName,
      });
      createModal.onClose();
      setFamName('');
      setFamDesc('');
      setRootDisplayName('');
      await loadFamilies();
      showQr(res.data.enrollment);
      toast({ title: 'Family created', status: 'success', duration: 3000 });
    } catch (err) {
      toast({
        title: 'Failed to create family',
        description: err instanceof ApiError ? err.message : undefined,
        status: 'error',
        duration: 4000,
      });
    }
  };

  const handleCreateInvite = async () => {
    if (!selected) return;
    try {
      const res = await adminApi.createEnrollmentToken(selected.id, {
        kind: 'enroll',
        displayName: invDisplayName,
        familyRole: invRole,
        expiresInDays: invDays,
      });
      inviteModal.onClose();
      setInvDisplayName('');
      setInvRole('member');
      await loadFamily(selected.id);
      showQr(res.data);
    } catch (err) {
      toast({
        title: 'Failed to create invite',
        description: err instanceof ApiError ? err.message : undefined,
        status: 'error',
        duration: 4000,
      });
    }
  };

  const notifyError = (err: unknown, title: string) => {
    toast({
      title,
      description: err instanceof ApiError ? err.message : 'Unknown error',
      status: 'error',
      duration: 4000,
    });
  };

  const handleRevoke = async (tokenId: string) => {
    if (!selected) return;
    try {
      await adminApi.revokeEnrollmentToken(selected.id, tokenId);
      await loadFamily(selected.id);
    } catch (err) {
      notifyError(err, 'Could not revoke the link');
    }
  };

  const handleRegenerate = async (tokenId: string) => {
    if (!selected) return;
    try {
      const res = await adminApi.regenerateEnrollmentToken(
        selected.id,
        tokenId
      );
      await loadFamily(selected.id);
      showQr(res.data);
    } catch (err) {
      notifyError(err, 'Could not regenerate the link');
    }
  };

  const handleDeleteFamily = async (id: string, name: string) => {
    if (!window.confirm(`Delete family "${name}" and all its data?`)) return;
    try {
      await adminApi.deleteFamily(id);
      setSelected(null);
      await loadFamilies();
    } catch (err) {
      notifyError(err, 'Could not delete the family');
    }
  };

  const loadingState = useLoadingState({
    loading: families === null && !list.error,
    error: list.error,
    retry: list.retry,
  });

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between">
        <Heading size="md">Families</Heading>
        <Button colorScheme="brand" size="sm" onClick={createModal.onOpen}>
          New family
        </Button>
      </HStack>

      <AdminSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by name or @handle"
        label="Search families"
      />

      {families === null || loadingState.show ? (
        <LoadingPanel state={loadingState} size="inline" />
      ) : (
        <VStack align="stretch" spacing={2}>
          <ListCount list={list} one="family" many="families" />
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Handle</Th>
                  <Th>Members</Th>
                  <Th>Created</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {families.map((f) => (
                  <Tr
                    key={f.id}
                    bg={selected?.id === f.id ? selectedRowBg : undefined}
                    cursor="pointer"
                    onClick={() => loadFamily(f.id)}
                  >
                    <Td fontWeight="medium">
                      {f.name}
                      {f.isDemo && <DemoTag />}
                    </Td>
                    <Td color="muted">@{f.handle}</Td>
                    <Td>{f.memberCount}</Td>
                    <Td>
                      {new Date(f.createdAt).toLocaleDateString(appLocale())}
                    </Td>
                    <Td textAlign="right">
                      <IconButton
                        aria-label="Delete family"
                        icon={<Trash2 size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFamily(f.id, `${f.name} (@${f.handle})`);
                        }}
                      />
                    </Td>
                  </Tr>
                ))}
                {families.length === 0 && (
                  <Tr>
                    <Td colSpan={5}>
                      <Text color="gray.500">
                        {search
                          ? 'No family matches that search.'
                          : 'No families yet.'}
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
          <LoadMore list={list} />
        </VStack>
      )}

      <DeletedFamilies />

      {selected && (
        <Box borderWidth={1} borderRadius="lg" p={4} bg={detailBg}>
          <HStack justify="space-between" mb={4}>
            <Heading size="sm">
              {selected.name} (@{selected.handle}) — members & invites
            </Heading>
            <Button
              size="sm"
              colorScheme="brand"
              variant="outline"
              onClick={inviteModal.onOpen}
            >
              New invite
            </Button>
          </HStack>

          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Members
          </Text>
          <Table size="sm" mb={4}>
            <Thead>
              <Tr>
                <Th>Handle</Th>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Last seen</Th>
              </Tr>
            </Thead>
            <Tbody>
              {selected.members.map((m) => (
                <Tr key={m.id}>
                  <Td>@{m.user.handle}</Td>
                  <Td>{m.user.displayName}</Td>
                  <Td>
                    <Badge
                      colorScheme={
                        m.role === 'root'
                          ? 'purple'
                          : m.role === 'admin'
                            ? 'orange'
                            : 'gray'
                      }
                    >
                      {m.role}
                    </Badge>
                  </Td>
                  <Td>
                    {m.user.lastSeenAt
                      ? new Date(m.user.lastSeenAt).toLocaleString(appLocale())
                      : 'never'}
                  </Td>
                </Tr>
              ))}
              {selected.members.length === 0 && (
                <Tr>
                  <Td colSpan={4}>
                    <Text color="gray.500">
                      No members yet — send the root QR.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Enrollment tokens
          </Text>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Kind</Th>
                <Th>Who</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Expires</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {selected.enrollmentTokens.map((t) => (
                <Tr key={t.id}>
                  <Td>New user</Td>
                  {/* Links since D348 hold no handle, only the name. */}
                  <Td>{t.handle ? `@${t.handle}` : t.displayName}</Td>
                  <Td>{t.familyRole}</Td>
                  <Td>
                    <Badge colorScheme={statusColor(t.status)}>
                      {t.status}
                    </Badge>
                  </Td>
                  <Td>
                    {new Date(t.expiresAt).toLocaleDateString(appLocale())}
                  </Td>
                  <Td textAlign="right">
                    {t.status === 'pending' && (
                      <HStack justify="flex-end" spacing={1}>
                        <Button
                          size="xs"
                          onClick={() => handleRegenerate(t.id)}
                        >
                          Regenerate
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleRevoke(t.id)}
                        >
                          Revoke
                        </Button>
                      </HStack>
                    )}
                    {/* A used link is never made again (D334). */}
                    {(t.status === 'expired' || t.status === 'revoked') && (
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => handleRegenerate(t.id)}
                      >
                        Re-issue
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
              {selected.enrollmentTokens.length === 0 && (
                <Tr>
                  <Td colSpan={6}>
                    <Text color="gray.500">No tokens yet.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create family modal */}
      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New family</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Family name</FormLabel>
                <Input
                  value={famName}
                  onChange={(e) => setFamName(e.target.value)}
                  placeholder="Sharma Family"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={famDesc}
                  onChange={(e) => setFamDesc(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>First root&apos;s display name</FormLabel>
                <Input
                  value={rootDisplayName}
                  onChange={(e) => setRootDisplayName(e.target.value)}
                  placeholder="Priya"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="brand"
              onClick={handleCreateFamily}
              isDisabled={!famName || !rootDisplayName}
            >
              Create & show QR
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New invite modal */}
      <Modal
        isOpen={inviteModal.isOpen}
        onClose={inviteModal.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New invite — {selected?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {/* New people only: nobody adds a device to someone else's
                  account, the system root included (D334, #105). */}
              <FormControl isRequired>
                <FormLabel>Display name</FormLabel>
                <Input
                  value={invDisplayName}
                  onChange={(e) => setInvDisplayName(e.target.value)}
                  placeholder="Priya"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Family role</FormLabel>
                <Select
                  value={invRole}
                  onChange={(e) =>
                    setInvRole(e.target.value as 'root' | 'admin' | 'member')
                  }
                >
                  <option value="member">member</option>
                  <option value="admin">admin</option>
                  <option value="root">root</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Expires in (days)</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={invDays}
                  onChange={(e) =>
                    setInvDays(parseInt(e.target.value, 10) || 7)
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="brand"
              onClick={handleCreateInvite}
              isDisabled={!invDisplayName}
            >
              Create & show QR
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <QrModal
        enrollment={qrEnrollment}
        isOpen={qrModal.isOpen}
        onClose={qrModal.onClose}
      />
    </VStack>
  );
}

// ---------- Users tab ----------

function UsersTab({ focusUserId }: { focusUserId?: string | null }) {
  const toast = useToast();
  const [appModes, setAppModes] = useState<AdminAppModes | null>(null);
  // Search by display name or handle, on the server, 300 ms after the last
  // keystroke; 50 a page (#73).
  const [query, setQuery] = useState('');
  const search = useDebounced(query.trim(), ADMIN_PAGE.SEARCH_DEBOUNCE_MS);
  const fetchPage = useCallback(
    async (cursor: string | null) =>
      (await adminApi.getUsers({ cursor, q: search })).data,
    [search]
  );
  const list = usePagedList<AdminUser, Awaited<ReturnType<typeof fetchPage>>>(
    fetchPage
  );
  // A report's person link (D210) may name someone not on the loaded pages:
  // fetch them alone and show them first.
  const [focused, setFocused] = useState<AdminUser | null>(null);
  const loaded = list.items;
  useEffect(() => {
    if (!focusUserId || !loaded) return;
    if (loaded.some((u) => u.id === focusUserId)) {
      setFocused(null);
      return;
    }
    let live = true;
    adminApi
      .getUsers({ id: focusUserId })
      .then((res) => {
        if (live) setFocused(res.data.items[0] ?? null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [focusUserId, loaded]);
  const users = loaded
    ? focused && focused.id === focusUserId
      ? [focused, ...loaded]
      : loaded
    : null;

  useEffect(() => {
    // The count is extra; the user list still loads without it (D186).
    adminApi
      .getAppModes()
      .then((modes) => setAppModes(modes.data))
      .catch(() => undefined);
  }, []);

  const handleDelete = async (id: string, handle: string) => {
    if (!window.confirm(`Delete @${handle} and all their devices/sessions?`))
      return;
    try {
      await adminApi.deleteUser(id);
    } catch (err) {
      toast({
        title: `Couldn't delete @${handle}`,
        description: err instanceof ApiError ? err.message : undefined,
        status: 'error',
        duration: 4000,
      });
      return;
    }
    if (focused?.id === id) setFocused(null);
    toast({ title: `@${handle} deleted`, status: 'success', duration: 3000 });
    await list.reload();
  };

  const loadingState = useLoadingState({
    loading: users === null && !list.error,
    error: list.error,
    retry: list.retry,
  });

  return (
    <VStack align="stretch" spacing={4}>
      <Heading size="md">Users</Heading>
      {appModes && (
        <Text fontSize="sm" color="gray.600">
          Installed app: {appModes.installedTotal} of {appModes.known} passkeys
          (last {appModes.days} days; {appModes.native} native,{' '}
          {appModes.installed} web app, {appModes.browser} browser only). One
          passkey can sign in on several devices; installed wins.
        </Text>
      )}
      <AdminSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by name or @handle"
        label="Search users"
      />
      {users === null || loadingState.show ? (
        <LoadingPanel state={loadingState} size="inline" />
      ) : (
        <VStack align="stretch" spacing={2}>
          <ListCount list={list} one="user" many="users" />
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Handle</Th>
                  <Th>Name</Th>
                  <Th>Families</Th>
                  <Th>Devices</Th>
                  <Th>Passkeys opened as</Th>
                  <Th>Last seen</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {users.map((u) => (
                  <Tr
                    key={u.id}
                    // A report's person link lands here (M58, D210).
                    bg={u.id === focusUserId ? 'accent.tint' : undefined}
                    ref={
                      u.id === focusUserId
                        ? (row: HTMLTableRowElement | null) =>
                            row?.scrollIntoView?.({ block: 'center' })
                        : undefined
                    }
                  >
                    <Td fontWeight="medium">@{u.handle}</Td>
                    <Td>
                      {u.displayName}
                      {u.isDemo && <DemoTag />}
                    </Td>
                    <Td>
                      <HStack spacing={1} flexWrap="wrap">
                        {u.families.map((f) => (
                          <Badge
                            key={f.id}
                            colorScheme={
                              f.role === 'root'
                                ? 'purple'
                                : f.role === 'admin'
                                  ? 'orange'
                                  : 'gray'
                            }
                          >
                            {f.name} ({f.role})
                          </Badge>
                        ))}
                      </HStack>
                    </Td>
                    <Td>{u.deviceCount}</Td>
                    <Td>
                      {u.appModes.length > 0 ? u.appModes.join(', ') : '—'}
                    </Td>
                    <Td>
                      {u.lastSeenAt
                        ? new Date(u.lastSeenAt).toLocaleString(appLocale())
                        : 'never'}
                    </Td>
                    <Td textAlign="right">
                      <IconButton
                        aria-label="Delete user"
                        icon={<Trash2 size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(u.id, u.handle)}
                      />
                    </Td>
                  </Tr>
                ))}
                {users.length === 0 && (
                  <Tr>
                    <Td colSpan={7}>
                      <Text color="gray.500">
                        {search
                          ? 'No one matches that search.'
                          : 'No users yet.'}
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
          <LoadMore list={list} />
        </VStack>
      )}
    </VStack>
  );
}

// ---------- Audit tab ----------

const fetchAuditPage = async (cursor: string | null) =>
  (await adminApi.getAuditLog({ cursor })).data;

function AuditTab() {
  // 50 a page, newest first (#73).
  const list = usePagedList<
    AdminAuditEntry,
    Awaited<ReturnType<typeof fetchAuditPage>>
  >(fetchAuditPage);
  const entries = list.items;

  const loadingState = useLoadingState({
    loading: entries === null && !list.error,
    error: list.error,
    retry: list.retry,
  });
  if (entries === null || loadingState.show)
    return <LoadingPanel state={loadingState} />;

  return (
    <VStack align="stretch" spacing={4}>
      <Heading size="md">Audit log</Heading>
      <ListCount list={list} one="entry" many="entries" />
      <Box overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>When</Th>
              <Th>Action</Th>
              <Th>Actor</Th>
              <Th>Target</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {entries.map((e) => (
              <Tr key={e.id}>
                <Td whiteSpace="nowrap">
                  {new Date(e.createdAt).toLocaleString(appLocale())}
                </Td>
                <Td>
                  <Badge title={e.action}>{auditActionLabel(e.action)}</Badge>
                </Td>
                <Td>
                  {e.actorIsSystemRoot
                    ? 'system root'
                    : e.actor
                      ? e.actor.handle
                        ? `@${e.actor.handle}`
                        : e.actor.displayName
                      : (e.actorUserId?.slice(0, 8) ?? '—')}
                </Td>
                <Td>
                  {e.targetType
                    ? `${e.targetType}:${e.targetId?.slice(0, 8)}`
                    : '—'}
                </Td>
                <Td fontSize="xs" color="gray.600" maxW="240px" isTruncated>
                  {e.metadata ? JSON.stringify(e.metadata) : '—'}
                </Td>
              </Tr>
            ))}
            {entries.length === 0 && (
              <Tr>
                <Td colSpan={5}>
                  <Text color="gray.500">No audit entries yet.</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      <LoadMore list={list} />
    </VStack>
  );
}

// ---------- Page ----------

/** The tab row's order; `/admin?tab=<name>` opens one by name (M67, D256). */
const TAB_ORDER = [
  'families',
  'users',
  'audit',
  'costs',
  'review',
  'requests',
  'ideas',
  'reports',
  'lawful',
  'health',
] as const;

export function AdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // New invitation requests, shown on the tab (M44, D116).
  const [newRequests, setNewRequests] = useState(0);
  // Unhandled reports (M58, D210), and where a report's links lead.
  const [newReports, setNewReports] = useState(0);
  const [tabIndex, setTabIndex] = useState(() =>
    Math.max(
      0,
      TAB_ORDER.indexOf(
        (searchParams.get('tab') ?? '') as (typeof TAB_ORDER)[number]
      )
    )
  );
  const [openFamilyId, setOpenFamilyId] = useState<string | null>(null);
  const [focusUserId, setFocusUserId] = useState<string | null>(null);

  // The badges come from the counts alone (one row each): the tabs
  // themselves load only when first opened (#73).
  useEffect(() => {
    if (!adminApi.isLoggedIn()) return;
    adminApi
      .getInviteRequests({ limit: 1 })
      .then((response) => setNewRequests(response.data.newCount))
      .catch(() => undefined);
    adminApi
      .getReports('unhandled', { limit: 1 })
      .then((response) => setNewReports(response.data.unhandledCount))
      .catch(() => undefined);
  }, []);

  if (!adminApi.isLoggedIn()) {
    return <Navigate to={`/admin/login${window.location.search}`} replace />;
  }

  return (
    <Box minH="100vh" bg="canvas">
      <Box bg="surface" borderBottomWidth={1} borderColor="line" px={6} py={3}>
        <HStack justify="space-between">
          <HStack spacing={3}>
            <LogoMark size={30} />
            <Heading as="h1" fontSize="22px">
              System root
            </Heading>
          </HStack>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            onClick={() => {
              adminApi.logout();
              navigate('/admin/login', { replace: true });
            }}
          >
            Sign out
          </Button>
        </HStack>
      </Box>
      <Container maxW="6xl" py={6}>
        <Tabs
          variant="soft-rounded"
          colorScheme="brand"
          index={tabIndex}
          onChange={setTabIndex}
          // A tab mounts (and fetches) the first time it's opened, then
          // stays mounted so it keeps its data and place (#73).
          isLazy
          lazyBehavior="keepMounted"
        >
          {/* The tabs don't fit a phone: scroll them sideways. */}
          <TabList
            overflowX="auto"
            overflowY="hidden"
            pb={1}
            sx={{ '& > button': { whiteSpace: 'nowrap', flexShrink: 0 } }}
          >
            <Tab>Families</Tab>
            <Tab>Users</Tab>
            <Tab>Audit log</Tab>
            <Tab>Costs</Tab>
            <Tab>Review access</Tab>
            <Tab>
              Requests
              {newRequests > 0 && (
                <Badge ml={2} colorScheme="orange" borderRadius="full">
                  {newRequests}
                </Badge>
              )}
            </Tab>
            <Tab>Ideas</Tab>
            <Tab>
              Reports
              {newReports > 0 && (
                <Badge ml={2} colorScheme="orange" borderRadius="full">
                  {newReports}
                </Badge>
              )}
            </Tab>
            {/* The sign-in log for lawful requests (M65, D244). */}
            <Tab>Lawful requests</Tab>
            {/* Monitoring (M67, D256). */}
            <Tab>Health</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <FamiliesTab openFamilyId={openFamilyId} />
            </TabPanel>
            <TabPanel>
              <UsersTab focusUserId={focusUserId} />
            </TabPanel>
            <TabPanel>
              <AuditTab />
            </TabPanel>
            <TabPanel>
              <CostsTab />
            </TabPanel>
            <TabPanel>
              <ReviewTab />
            </TabPanel>
            <TabPanel>
              <RequestsTab onCount={setNewRequests} />
            </TabPanel>
            <TabPanel>
              <IdeasTab />
            </TabPanel>
            <TabPanel>
              <ReportsTab
                onCount={setNewReports}
                onOpenFamily={(id) => {
                  setOpenFamilyId(id);
                  setTabIndex(0);
                }}
                onOpenUser={(id) => {
                  setFocusUserId(id);
                  setTabIndex(1);
                }}
              />
            </TabPanel>
            <TabPanel>
              <LawfulRequestsTab />
            </TabPanel>
            <TabPanel>
              <HealthTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
    </Box>
  );
}
